import { useState, useRef, useEffect } from "react";
import AddUserIcon from "../../../../../../../../assets/svg/add-user-icon";
import AgentIcon from "../../../../../../../../assets/svg/agent-icon";
import Input from "../../../../../../../../components/Input/Input";
import PhoneNumberCountryCodeSelector from "../../../../../../../../components/PhoneNumberCountryCodeSelector/PhoneNumberCountryCodeSelector";
import SearchInput from "../../../../../../../../components/SearchableDropdown/components/SearchInput";
import ToggleSwitch from "../../../../../../../../components/ToggleSwitch/ToggleSwitch";
import { visibleScrollbar } from "../../../../../../../../utils/functions/get-visible-scrollbar";
import { useGetCurrentClientsQuery } from "../../../../../../../../redux/services/userManagementApi";
import { FormikProps } from "formik";
import { UserDetailsValues } from "../../../utils/types";
import {
  formatPhoneNumber,
  sortCountryOptions,
  transformCountryOptions,
} from "../../../../../../../../components/PhoneNumberCountryCodeSelector/utils/phoneNumberUtils";
import { countryOptions } from "../../../../../../../../utils/constants/countryOptions";
import { CountryOption } from "../../../../../../../../components/PhoneNumberCountryCodeSelector/utils/types";
import { layoutOptions } from "./utils/constants";
import UserDetailsShimmer from "./UserDetailsShimmer";
import { getClientDomain } from "../../../../../../../../utils/functions/client-code";
import WebsiteIcon from "../../../../../../../../assets/svg/website-icon";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../../../redux/store";
import { CREATE_USER_SELECT_ORGANIZATION } from "../../../../../../utils/constants";

interface Client {
  client_code: string;
  client_name: string;
  client_logo: string;
}
interface UserDetailsProps {
  formik: FormikProps<UserDetailsValues>;
}

export const UserDetails = ({ formik }: UserDetailsProps) => {
  const { data: clientsData, isLoading: isClientsLoading } =
    useGetCurrentClientsQuery({});
  const fontSize = useSelector(
    (state: RootState) => state?.fontSettings?.fontSize,
  );

  const [orgLayout, setOrgLayout] = useState<"grid" | "list">("grid");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [phoneInput, setPhoneInput] = useState("");

  const searchInputRef = useRef<HTMLInputElement>(null);

  const clientCode = getClientDomain();
  const organizations = formik?.values?.organizations;
  const organizationsCount = organizations?.length;
  const transformedCountryOptions = transformCountryOptions(countryOptions);
  const sortedCountryOptions = sortCountryOptions(transformedCountryOptions);
  const filteredClients =
    clientsData?.filter((client: Client) =>
      client?.client_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()),
    ) || [];

  const shouldCountErrors =
    formik?.submitCount > 0 || Object.keys(formik?.touched || {}).length > 0;

  const errorLength = shouldCountErrors
    ? Object?.keys(formik?.errors || {})?.length
    : 0;

  const selectOrganizationHeight = CREATE_USER_SELECT_ORGANIZATION(
    errorLength,
    fontSize,
  );

  const handleOrgLayoutChange = (layout: "grid" | "list") => {
    setOrgLayout(layout);
  };

  const isClientSelected = (client: Client) => {
    return organizations?.find(
      (org) => org?.client_code === client?.client_code,
    );
  };

  const handleOrganizationToggle = (client: Client) => {
    if (client?.client_code === clientCode) {
      return;
    }

    const isSelected = isClientSelected(client);

    formik?.setFieldValue(
      "organizations",
      isSelected
        ? organizations?.filter((org) => org.client_code !== client.client_code)
        : [...(organizations || []), client],
    );
  };

  const handleAgentToggle = () => {
    formik?.setFieldValue("isAgent", !formik?.values?.isAgent);
  };

  const handleInternationalAgentToggle = () => {
    formik?.setFieldValue(
      "isInternationalAgent",
      !formik?.values?.isInternationalAgent,
    );
  };

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const digitsOnly = inputValue.replace(/\D/g, "");
    const formattedNumber = formatPhoneNumber(
      digitsOnly,
      formik.values.phoneCountryCode,
    );
    setPhoneInput(formattedNumber);
    formik.setFieldValue("phoneNumber", digitsOnly);
  };

  const handlePhoneCountryChange = (country: CountryOption) => {
    formik.setFieldValue("phoneCountryCode", country);

    if (formik?.values?.phoneNumber) {
      const digitsOnly = phoneInput.replace(/\D/g, "");
      const formattedNumber = formatPhoneNumber(digitsOnly, country);
      setPhoneInput(formattedNumber);
      formik.setFieldValue("phoneNumber", digitsOnly);
    }
  };

  useEffect(() => {
    if (formik?.values?.phoneNumber && formik?.values?.phoneCountryCode) {
      const formattedNumber = formatPhoneNumber(
        formik.values.phoneNumber,
        formik.values.phoneCountryCode,
      );
      setPhoneInput(formattedNumber);
    }
  }, [formik?.values?.phoneNumber, formik?.values?.phoneCountryCode]);

  useEffect(() => {
    if (!clientsData || !clientCode || isClientsLoading) return;
    const client = clientsData?.find(
      (c: Client) => c?.client_code === clientCode,
    );
    const orgs = formik?.values?.organizations ?? [];
    if (client && !orgs?.some((o) => o?.client_code === clientCode)) {
      formik?.setFieldValue("organizations", [
        ...orgs,
        { client_code: client?.client_code, client_name: client?.client_name },
      ]);
    }
  }, [clientsData, clientCode, isClientsLoading, formik]);

  return (
    <>
      <div className="flex items-center gap-2 my-[18px]">
        <AddUserIcon className="w-6 h-6 text-verba-blue dark:text-verba-blue-dk" />
        <div className="flex flex-col">
          <span className="text-base text-heading-text dark:text-heading-text-dk">
            Create new user, one at a time!
          </span>
          <span className="text-xs text-dark-gray dark:text-dark-gray-dk">
            Add a new user to your VerbaFlo dashboard
          </span>
        </div>
      </div>
      <div className="flex flex-col w-[264px] gap-y-[14px]">
        <Input
          label="Name"
          name="name"
          id="name"
          placeholder="Enter name"
          containerClassName="w-full"
          labelClassName="text-verba-field-label dark:text-verba-field-label-dk text-md"
          secondaryContainerClassName="px-[8px] py-[8px] rounded-lg border border-border-gray dark:border-border-gray-dk"
          inputClassName="text-md placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
          value={formik?.values?.name}
          onChange={formik?.handleChange}
          onBlur={formik?.handleBlur}
          error={formik?.touched?.name && !!formik?.errors?.name}
          errorMessage={formik?.errors?.name}
        />
        <Input
          label="Email"
          name="email"
          id="email"
          placeholder="name@example.com"
          containerClassName="w-full"
          labelClassName="text-verba-field-label dark:text-verba-field-label-dk text-md"
          secondaryContainerClassName="px-[8px] py-[8px] rounded-lg border border-border-gray dark:border-border-gray-dk"
          inputClassName="text-md placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
          value={formik?.values?.email}
          onChange={formik?.handleChange}
          onBlur={formik?.handleBlur}
          error={formik?.touched?.email && !!formik?.errors?.email}
          errorMessage={formik?.errors?.email}
        />
        <PhoneNumberCountryCodeSelector
          label="Phone Number"
          labelClassName="text-verba-field-label dark:text-verba-field-label-dk font-normal text-md"
          countryOptions={sortedCountryOptions}
          selectedCountry={formik?.values?.phoneCountryCode}
          onCountryChange={handlePhoneCountryChange}
          phoneInput={phoneInput}
          onPhoneInputChange={handlePhoneInputChange}
          validationError={
            formik.touched.phoneNumber ? formik.errors.phoneNumber : ""
          }
          containerClassName="w-full"
          countryDropdownClassName="w-[250px] min-w-[250px] max-h-[250px]"
          countryDropdownSelectedOptionClassName="h-[34px] px-[8px] py-[9px]"
          phoneInputClassName="h-[36px] px-[8px] py-[6px] rounded-[6px] bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk"
          countryDropdownPlaceholder="Select Country"
          countryDropdownSearchPlaceholder="Search countries..."
          countryDropdownSectionHeading="All Countries"
        />
      </div>
      <div className="flex flex-col gap-y-3 my-8">
        <div className="w-[264px] flex items-center justify-between rounded-lg border border-border-gray dark:border-border-gray-dk py-[9px] px-[6px]">
          <div className="flex items-center gap-1">
            <AgentIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
            <span className="text-heading-text dark:text-heading-text-dk text-sm">
              This user is an Agent
            </span>
          </div>
          <ToggleSwitch
            isChecked={formik?.values?.isAgent}
            handleToggle={handleAgentToggle}
          />
        </div>
        <div className="w-[264px] flex items-center justify-between rounded-lg border border-border-gray dark:border-border-gray-dk py-[9px] px-[6px]">
          <div className="flex items-center gap-1">
            <WebsiteIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
            <span className="text-heading-text dark:text-heading-text-dk text-sm">
              International Agent
            </span>
          </div>
          <ToggleSwitch
            isChecked={formik?.values?.isInternationalAgent}
            handleToggle={handleInternationalAgentToggle}
          />
        </div>
      </div>
      {isClientsLoading ? (
        <UserDetailsShimmer orgLayout={orgLayout} orgCount={8} />
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-base text-heading-text dark:text-heading-text-dk">
                Select Organisations
              </span>
              {(organizationsCount ?? 0) > 0 && (
                <div className="h-[18px] px-1 flex items-center justify-center text-xxs leading-0 text-verba-indigo dark:text-verba-indigo-dk rounded-sm border border-gray-100 dark:border-gray-100-dk">
                  {organizationsCount} Selected
                </div>
              )}
            </div>
            <div className="flex items-center p-0.5 border border-border-gray dark:border-border-gray-dk rounded-sm">
              {layoutOptions?.map(({ type, icon: Icon, label }) => (
                <div
                  key={type}
                  className={`px-0.5 py-px cursor-pointer rounded-sm ${
                    orgLayout === type &&
                    "bg-verba-indigo dark:bg-verba-indigo-dk"
                  }`}
                  onClick={() => handleOrgLayoutChange(type)}
                  title={label}
                >
                  <Icon
                    className={`w-[18px] h-[18px] ${
                      orgLayout === type
                        ? "text-pure-white dark:text-pure-white-dk"
                        : "text-verba-indigo dark:text-verba-indigo-dk"
                    }`}
                  />
                </div>
              ))}
            </div>
          </div>
          <SearchInput
            searchQuery={searchTerm}
            searchPlaceholder="Search"
            searchInputRef={searchInputRef}
            onSearchQueryChange={setSearchTerm}
            showSearch={true}
            inputFieldClass="text-md placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
            className="w-[264px] h-[30px] px-2! mt-2 rounded-md border border-border-gray dark:border-border-gray-dk"
            searchIconClassName="!w-4 !h-[4"
          />
          {formik?.touched?.organizations && formik?.errors?.organizations && (
            <div className="text-xxs text-verba-red-alert dark:text-verba-red-alert-dk mt-[3px]">
              {formik?.errors?.organizations}
            </div>
          )}
          <div
            className={`grid content-start gap-2 overflow-y-auto mt-3.5 pb-2 ${
              orgLayout === "grid" ? "grid-cols-2" : "grid-cols-1"
            } ${visibleScrollbar("[&::-webkit-scrollbar]:w-[7px]")}`}
            style={{ maxHeight: selectOrganizationHeight }}
          >
            {filteredClients?.length > 0 ? (
              filteredClients?.map((organization: Client) => {
                const isSelected =
                  !!isClientSelected(organization) ||
                  organization?.client_code === clientCode;
                return (
                  <div
                    key={organization?.client_code}
                    className={`p-2 flex items-center justify-between rounded-lg cursor-pointer border ${
                      isSelected
                        ? "border-verba-blue/20 dark:border-verba-blue-dk/20 bg-card-highlight-2 dark:bg-card-highlight-2-dk"
                        : "border-border-gray dark:border-border-gray-dk"
                    }`}
                    onClick={() => handleOrganizationToggle(organization)}
                  >
                    <div className="flex items-center gap-1">
                      <img
                        src={organization?.client_logo}
                        className="w-[18px] h-[18px] object-cover"
                        alt={`${organization?.client_name} Logo`}
                      />
                      <span className="font-medium text-sm text-heading-text dark:text-heading-text-dk">
                        {organization?.client_name}
                      </span>
                    </div>
                    <input
                      type="checkbox"
                      className="w-[12px] h-[12px] cursor-pointer"
                      checked={isSelected}
                      onChange={() => handleOrganizationToggle(organization)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                );
              })
            ) : (
              <div className="col-span-full text-center py-8">
                <span className="text-sm text-dark-gray dark:text-dark-gray-dk">
                  {searchTerm
                    ? "No organisations found"
                    : "No organisations available"}
                </span>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};
